import * as React from 'react'

import { ChevronDown, ChevronLeft, ChevronRight, XIcon } from 'lucide-react'
import { action, computed, makeObservable, observable } from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { IWorkingHours, WorkflowDurationType } from '~/client/graph'
import { Button } from '~/client/src/shared/components/Button/Button'

import Localization from '../../localization/LocalizationManager'
import ProjectDateStore, {
  isBefore,
  NonWorkTimeReason,
} from '../../stores/ui/ProjectDate.store'
import {
  getNonWorkTimeMessages,
  MIDDLE_NON_WORK_REASONS,
} from '../../utils/nonWorkTimeMessages'
import { notifyOverlayOpen } from '../../utils/overlayNotifier'
import CommonDatePicker from '../CommonDatePicker/CommonDatePicker'
import { getDefaultStartAndFinishDates } from '../CommonDatePicker/CommonDatePickerHelper'
import MonthPicker from '../CommonDatePicker/MonthPicker/MonthPicker'
import Dimmer from '../Dimmer'
import { Checkbox } from '../shadcn-ui/checkbox'
import StruxhubInput from '../StruxhubInputs/StruxhubInput'

import './PermitDatePicker.scss'

interface IProps {
  initialStartDate: number
  initialEndDate: number
  initialIsAllDay?: boolean
  shouldShowTimePicker: boolean
  applyDates: (startDate: Date, endDate: Date, isAllDay?: boolean) => void
  hideDatePicker: () => void

  isOneDayMode?: boolean
  maxRangeInYears?: number

  maxDurationType?: WorkflowDurationType
  maxDuration?: number
  maxDurationMessage?: string

  projectDateStore?: ProjectDateStore

  shouldDateBeDisabled?: (date: Date) => boolean
}

const selectDate = (isOneDayMode: boolean) =>
  isOneDayMode ? 'Select date' : 'Select dates'
const maxDatesRangeInYearsIs = (maxRange: number) =>
  `Maximum date range exceeded. The maximum date range in years is - ${maxRange}`

const select = 'Select'
const start = 'Start'
const finish = 'Finish'
const nonWorkDay = 'Non-work day'
const nonWorkHours = 'Non-work hours'

const DEFAULT_TIME = '00:00'

@inject('projectDateStore')
@observer
export default class PermitDatePicker extends React.Component<IProps> {
  @observable private startDate: Date = null
  @observable private endDate: Date = null
  @observable private startTime: string = DEFAULT_TIME
  @observable private endTime: string = DEFAULT_TIME
  @observable private selectedMonth: Date = null
  @observable private isMonthPickerShown: boolean = false
  @observable private isAllDay: boolean = null
  @observable private localProps: IProps = null

  private closeOverlay: (() => void) | null = null

  public constructor(props: IProps) {
    super(props)

    this.setInitialDates()
    this.localProps = props

    makeObservable(this)
  }

  public componentDidMount() {
    this.closeOverlay = notifyOverlayOpen()
  }

  public componentWillUnmount() {
    this.closeOverlay?.()
  }

  public componentDidUpdate(prevProps: Readonly<IProps>) {
    const { initialStartDate, initialEndDate } = this.props

    if (
      prevProps.initialStartDate !== initialStartDate ||
      prevProps.initialEndDate !== initialEndDate
    ) {
      this.setInitialDates()
      this.localProps.initialEndDate = this.props.initialEndDate
      this.localProps.initialStartDate = this.props.initialStartDate
    }
  }

  public render() {
    const {
      hideDatePicker,
      projectDateStore: { getMonthAndYearToDisplay },
      isOneDayMode,
      maxRangeInYears,
      shouldShowTimePicker,
      shouldDateBeDisabled,
    } = this.props

    return (
      <div className="permit-date-picker-container">
        <Dimmer shown={true} onClick={hideDatePicker} />
        <div className="permit-date-picker bg-white no-select col full-width overflow-auto">
          <div className="tw:flex tw:items-center tw:px-3 tw:pt-[15px]">
            <Button
              variant="ghost"
              size="icon"
              className="tw:shrink-0"
              onClick={hideDatePicker}
              aria-label={Localization.translator.close}
            >
              <XIcon aria-hidden="true" />
            </Button>
            <div className="text bold small-header tw:mr-[30px] tw:flex-1 tw:text-center">
              {selectDate(isOneDayMode)}
            </div>
          </div>
          <div className="row px16 my20">
            <div>
              <div onClick={this.showMonthPicker}>
                <span className="text bold extra-large line-extra-large lp015 primary">
                  {getMonthAndYearToDisplay(this.selectedMonth)}
                </span>
                <ChevronDown className="tw:inline tw:size-4 tw:align-middle" />
              </div>
              <MonthPicker
                shown={this.isMonthPickerShown}
                value={this.selectedMonth}
                onHide={this.hideMonthPicker}
                onChange={this.updateCurrentMonth}
              />
            </div>
            <div className="row x-end no-flex-children">
              <Button
                variant="ghost"
                size="icon"
                className="mr5"
                onClick={this.prevMonth}
                aria-label={Localization.translator.previous}
              >
                <ChevronLeft aria-hidden="true" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="mr5"
                onClick={this.nextMonth}
                aria-label={Localization.translator.next}
              >
                <ChevronRight aria-hidden="true" />
              </Button>
            </div>
          </div>
          <CommonDatePicker
            monthDate={this.selectedMonth}
            dateClickHandler={this.onDateClick}
            startDate={this.startDate}
            endDate={this.endDate}
            isOneDayMode={isOneDayMode}
            shouldDateBeDisabled={shouldDateBeDisabled}
          />
          {!isOneDayMode && (
            <>
              {!this.isDatesRangeValid && !this.isEndDateInvalid && (
                <div className="ml5 text bold red large py5 px16">
                  {maxDatesRangeInYearsIs(maxRangeInYears)}
                </div>
              )}
              {shouldShowTimePicker && this.renderAllDayCheckbox()}
              <div className="bt-light-grey row y-center mt10 py10 px16">
                {this.renderDateAndTimeFields(
                  'br-palette-brand-lighter pr8',
                  start,
                  this.startDate,
                  this.startTime,
                  this.onStartTimeChange,
                )}
                {this.renderDateAndTimeFields(
                  'pl8',
                  finish,
                  this.endDate,
                  this.endTime,
                  this.onEndTimeChange,
                )}
              </div>
              {this.renderNonWorkIntervalLabels()}
              {this.renderMaxDurationValidationLabel()}
              {this.renderIncorrectDatesMessage()}
            </>
          )}
          <div className="px16 mt5">
            <div
              onClick={this.applyDates}
              className={classList({
                'h40 brada4 bg-palette-blue-scale row x-center y-center text white extra-large bold': true,
                'inactive-element': !this.areDatesValid,
              })}
            >
              {select}
            </div>
          </div>
        </div>
      </div>
    )
  }

  /**
   * What the two field labels cannot say. Each of those speaks for one end, so a
   * holiday, an unworked day or a closed stretch *between* the ends went
   * unreported here even while the field under the picker named it (STX1-983).
   */
  private renderNonWorkIntervalLabels(): JSX.Element {
    const { startDate, endDate, startTime, endTime } = this
    if (
      !startDate ||
      !endDate ||
      !startTime ||
      !endTime ||
      this.isEndDateInvalid
    ) {
      return null
    }

    const reasons = this.props.projectDateStore.getNonWorkTimeReasons(
      this.getDateTime(startDate, startTime),
      this.getDateTime(endDate, endTime),
    )

    // An interval reason shares its wording with the endpoint reason of the same
    // kind, and that one is already printed beside the field it belongs to — so
    // an out-of-hours start would otherwise say "Non-work hours" next to the
    // input and again beneath it. Matched on the reason rather than the rendered
    // text: the field labels are not translated, so comparing strings would stop
    // matching outside English.
    const isAlreadySaidBesideAField = (reason: NonWorkTimeReason): boolean =>
      reasons.includes(
        reason === NonWorkTimeReason.MiddleDay
          ? NonWorkTimeReason.EndpointDay
          : NonWorkTimeReason.EndpointHours,
      )

    const messages = getNonWorkTimeMessages(
      reasons.filter(
        reason =>
          MIDDLE_NON_WORK_REASONS.includes(reason) &&
          !isAlreadySaidBesideAField(reason),
      ),
    )

    return (
      <>
        {messages.map(message => (
          <div key={message} className="ml5 text red large py5 px16">
            {message}
          </div>
        ))}
      </>
    )
  }

  private renderMaxDurationValidationLabel(): JSX.Element {
    const { maxDurationType, maxDuration, maxDurationMessage } = this.props
    const { startDate, endDate, startTime, endTime } = this
    if (
      !startDate ||
      !endDate ||
      !startTime ||
      !endTime ||
      !maxDurationType ||
      maxDuration === null ||
      this.isEndDateInvalid
    ) {
      return null
    }

    const startDateTime = this.getDateTime(startDate, startTime)
    const endDateTime = this.getDateTime(endDate, endTime)

    return this.props.projectDateStore.validateMaxDuration(
      startDateTime,
      endDateTime,
      maxDurationType,
      maxDuration,
    ) ? null : (
      <div className="ml5 text red large py5 px16">{maxDurationMessage}</div>
    )
  }

  private renderIncorrectDatesMessage(): JSX.Element {
    if (this.isDatesRangeValid || !this.isEndDateInvalid) {
      return null
    }

    return (
      <div className="ml5 text center bold red large py5 px16">
        {Localization.translator.incorrectDateOrTime}
      </div>
    )
  }

  @action.bound
  public onDateClick(_: Event, date: Date) {
    this.setDates(date)
  }

  /**
   * What All day fills on the selected date — its own shift, or the remainder of
   * last night's when the date is only reached by one. Every reader here asks the
   * same question, and they have to: taking the times from one window while
   * deciding the midnight roll from another would put an overnight day's end
   * before its own start (STX1-983).
   */
  private get allDayWindow(): IWorkingHours {
    return this.props.projectDateStore.getAllDayWindowForDate(this.startDate)
  }

  /** Whether that window finishes on the day after the selected one. */
  private get doesAllDayEndNextDate(): boolean {
    const { startTime, endTime } = this.allDayWindow

    return !this.props.projectDateStore.isSameDay(startTime, endTime)
  }

  @action.bound
  public setTimesToWorkingHours() {
    const { getISOTime } = this.props.projectDateStore
    const { startTime, endTime } = this.allDayWindow

    this.startTime = getISOTime(startTime)
    this.endTime = getISOTime(endTime)
  }

  @action.bound
  public setSingleDay() {
    const { isSameDay } = this.props.projectDateStore

    if (!isSameDay(this.startDate, this.endDate)) this.endDate = this.startDate
  }

  @action.bound
  public onAllDayChange() {
    this.isAllDay = !this.isAllDay

    if (!this.isAllDay) return

    const endsNextDate = this.doesAllDayEndNextDate

    this.setTimesToWorkingHours()
    this.setSingleDay()

    if (endsNextDate) {
      this.endDate = this.props.projectDateStore.addDays(this.endDate, 1)
    }
  }

  @action.bound
  public resetIsAllDay() {
    const { isSameDay, getISOTime, addDays } = this.props.projectDateStore
    const { startTime, endTime } = this.allDayWindow

    // A shift running past midnight finishes on the following date, so the
    // interval that fills it spans two — asking for the same day would leave
    // such a day unable to read as all-day at all.
    const expectedEndDate = this.doesAllDayEndNextDate
      ? addDays(this.startDate, 1)
      : this.startDate

    this.isAllDay =
      this.startTime === getISOTime(startTime) &&
      this.endTime === getISOTime(endTime) &&
      isSameDay(this.endDate, expectedEndDate) &&
      isBefore(this.startDate, this.endDate)
  }

  @action.bound
  private setDates(date: Date) {
    const newDates = getDefaultStartAndFinishDates(
      date,
      this.startDate,
      this.endDate,
      this.props.isOneDayMode,
    )
    this.startDate = newDates.startDate
    this.endDate = newDates.endDate

    this.resetIsAllDay()
  }

  private renderAllDayCheckbox(): JSX.Element {
    return (
      <div
        className="tw:flex tw:cursor-pointer tw:items-center tw:gap-2 tw:pt-2.5 tw:pl-4"
        onClick={this.onAllDayChange}
      >
        <Checkbox checked={this.isAllDay} />
        <span className="text large">{Localization.translator.allDay}</span>
      </div>
    )
  }

  private renderDateAndTimeFields(
    className: string,
    caption: string,
    date: Date,
    time: string,
    onTimeChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
  ): JSX.Element {
    const { shouldShowTimePicker, projectDateStore } = this.props
    const { getWeekdayMonthDayAndYearToDisplay } = projectDateStore

    return (
      <div className={`col ${className}`}>
        <div className="text faded">{caption.toUpperCase()}</div>
        <div className="blue-highlight text large bold pb10 pt5">
          {getWeekdayMonthDayAndYearToDisplay(date) || '-'}
        </div>
        {shouldShowTimePicker && (
          <>
            <StruxhubInput
              type="time"
              value={time}
              onChange={onTimeChange}
              isMinimalisticMode={true}
            />
            <div className="ml5 text red large h20">
              {this.getNonWorkLabel(date, time)}
            </div>
          </>
        )}
      </div>
    )
  }

  private get areDatesValid(): boolean {
    return !!this.startDate && !!this.endDate && this.isDatesRangeValid
  }

  @computed
  private get isDatesRangeValid(): boolean {
    const {
      maxRangeInYears,
      projectDateStore: { endOfDay, addYears },
      isOneDayMode,
    } = this.localProps

    if (isOneDayMode || !this.startDate || !this.endDate || !maxRangeInYears) {
      return true
    }

    if (this.isEndDateInvalid) {
      return false
    }

    return (
      endOfDay(this.endDate) <=
      endOfDay(addYears(this.startDate, maxRangeInYears, true))
    )
  }

  @computed
  public get isEndDateInvalid(): boolean {
    if (this.localProps.isOneDayMode) {
      return false
    }

    const startDate = this.getDateTime(this.startDate, this.startTime)
    const endDate = this.getDateTime(this.endDate, this.endTime)

    return (
      isBefore(endDate, startDate) ||
      this.localProps.projectDateStore.areDatesEqualToMinutes(
        endDate,
        startDate,
      )
    )
  }

  @action
  private applyDates = () => {
    if (!this.areDatesValid) {
      return
    }
    const { applyDates, hideDatePicker } = this.props
    this.startDate = this.getDateTime(this.startDate, this.startTime)
    this.endDate = this.getDateTime(this.endDate, this.endTime)
    applyDates(this.startDate, this.endDate, this.isAllDay)
    hideDatePicker()
  }

  @action
  private onStartTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.startTime = event.target.value

    this.resetIsAllDay()
  }

  @action
  private onEndTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.endTime = event.target.value

    this.resetIsAllDay()
  }

  private getDateTime = (date: Date, timeToSet: string): Date => {
    const { getDashedFormattedDate, combineISODateTime } =
      this.localProps.projectDateStore
    return combineISODateTime(getDashedFormattedDate(date), timeToSet)
  }

  private getNonWorkLabel = (date: Date, time: string): string => {
    if (!date || !time) {
      return null
    }

    const { areTimesInsideWorkingHours, hasWorkingHours } =
      this.props.projectDateStore

    const dateTime = this.getDateTime(date, time)

    // "No worked span at all" is the non-working-day label; a date that is
    // worked at other times gets the non-working-hours one. Testing the weekday
    // would call a Tuesday non-working even when Monday's overnight window
    // reaches into it (STX1-983).
    const isNonWorkDay = !hasWorkingHours(dateTime)
    const isNonWorkTime = !areTimesInsideWorkingHours(dateTime, dateTime)

    return isNonWorkDay ? nonWorkDay : isNonWorkTime && nonWorkHours
  }

  @action.bound
  private setInitialDates() {
    const {
      initialStartDate,
      initialEndDate,
      initialIsAllDay,
      projectDateStore,
    } = this.props

    this.startDate = new Date(initialStartDate)
    this.startTime = projectDateStore.getISOTime(this.startDate)
    this.endDate = new Date(initialEndDate)
    this.endTime = projectDateStore.getISOTime(this.endDate)
    this.isAllDay = initialIsAllDay

    this.selectedMonth = new Date(initialStartDate)
  }

  @action.bound
  private showMonthPicker() {
    this.isMonthPickerShown = true
  }

  @action.bound
  private hideMonthPicker() {
    this.isMonthPickerShown = false
  }

  @action.bound
  private prevMonth() {
    this.updateCurrentMonth(
      this.props.projectDateStore.addMonths(this.selectedMonth, -1),
    )
  }

  @action.bound
  private nextMonth() {
    this.updateCurrentMonth(
      this.props.projectDateStore.addMonths(this.selectedMonth, 1),
    )
  }

  @action.bound
  private updateCurrentMonth(month: Date) {
    this.selectedMonth = month
  }
}
